import { z } from "zod";
import { api, tokenStore } from "./client";
import { SessionDetail, SessionSummary, type SessionSettings, type TranscriptItem } from "./agent";

/* ──────────────────────────────────────────────────────────────────────
   Mirrors api/routers/agent3.py (SuperAdmin). Only what is new for /agent3:
   v3 sessions, messages with images, and the live capture browser. Everything
   else about a v3 session (get, patch, approve, events, cancel, compact) uses
   the /api/agent endpoints in ./agent.
   ────────────────────────────────────────────────────────────────────── */

export const MAX_IMAGES = 3;

/** A transcript row as /agent3 renders it: the shared shape plus images. */
export interface Transcript3Item extends Omit<TranscriptItem, "type"> {
  type: TranscriptItem["type"] | "image";
  /** user rows: images the operator attached (files served by the API) */
  images?: Array<{ name: string; bytes?: number; mime?: string }>;
  /** image rows: a screenshot / page image a TOOL showed the model */
  data_url?: string;
  /** optimistic user rows: the data URLs before the server has stored them */
  local_images?: string[];
}

export const LiveBrowser = z.object({
  channel_id:   z.number(),
  status:       z.string().nullable().optional(),
  mode:         z.string().nullable().optional(),
  channel_name: z.string().nullable().optional(),
  message:      z.string().nullable().optional(),
  novnc_url:    z.string().nullable().optional(),
  captured:     z.number().nullable().optional(),
  owner:        z.record(z.unknown()).nullable().optional(),
  owner_text:   z.string().optional(),
  drivable:     z.boolean(),
});
export type LiveBrowser = z.infer<typeof LiveBrowser>;

export async function listSessions3(): Promise<SessionSummary[]> {
  const r = await api.get("/api/agent3/sessions");
  return z.array(SessionSummary).parse((r.data as { sessions: unknown }).sessions);
}

export async function createSession3(title?: string): Promise<SessionDetail> {
  const r = await api.post("/api/agent3/sessions", { title: title ?? null });
  return SessionDetail.parse({ ...(r.data as object), pending_approvals: [], running: false });
}

export async function deleteSession3(id: string): Promise<void> {
  await api.delete(`/api/agent3/sessions/${encodeURIComponent(id)}`);
}

export async function steerRun3(id: string, content: string, expectedTurn?: string | null): Promise<{ queued: number; turn_id: string }> {
  const r = await api.post(`/api/agent3/sessions/${encodeURIComponent(id)}/steer`, { content, expected_turn: expectedTurn ?? null });
  return r.data as { queued: number; turn_id: string };
}

/** An attached image, fetched with the bearer token (an <img src> cannot send it). */
export async function fetchSessionImage(id: string, name: string): Promise<Blob> {
  const r = await api.get(`/api/agent3/sessions/${encodeURIComponent(id)}/images/${encodeURIComponent(name)}`,
                          { responseType: "blob" });
  return r.data as Blob;
}

export async function fetchLiveBrowser(): Promise<{ live: LiveBrowser[]; active: LiveBrowser | null }> {
  const r = await api.get("/api/agent3/browser", { timeout: 30_000 });
  const d = r.data as { live: unknown[]; active: unknown };
  return { live: z.array(LiveBrowser).parse(d.live), active: d.active ? LiveBrowser.parse(d.active) : null };
}

export async function openLiveBrowser(channelId: number, mode: "login" | "navigate", startUrl?: string): Promise<Record<string, unknown>> {
  const r = await api.post("/api/agent3/browser/open",
    { channel_id: channelId, mode, start_url: startUrl?.trim() || null }, { timeout: 90_000 });
  return r.data as Record<string, unknown>;
}

export interface Screenshot { data_url: string; url?: string; title?: string; width?: number; height?: number; channel_id: number }

export async function liveScreenshot(channelId?: number): Promise<Screenshot> {
  const r = await api.post("/api/agent3/browser/screenshot", { channel_id: channelId ?? null }, { timeout: 90_000 });
  return r.data as Screenshot;
}

export async function liveSignal(action: "loggedin" | "done" | "cancel", channelId?: number): Promise<Record<string, unknown>> {
  const r = await api.post("/api/agent3/browser/signal", { channel_id: channelId ?? null, action }, { timeout: 90_000 });
  return r.data as Record<string, unknown>;
}

/** Start a turn from a message (+ images) and watch it — same SSE contract as /api/agent. */
export async function streamMessage3(
  sessionId: string, content: string, images: string[],
  onEvent: (ev: Record<string, unknown>) => void, signal?: AbortSignal, settings?: SessionSettings,
): Promise<void> {
  const url = `/api/agent3/sessions/${encodeURIComponent(sessionId)}/messages`;
  const body = JSON.stringify({ content, images, ...(settings ?? {}) });
  const attempt = async (retry: boolean): Promise<void> => {
    const res = await fetch(url, {
      method: "POST", body, signal,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tokenStore.access ?? ""}` },
    });
    if (res.status === 401 && retry) { await api.get("/api/auth/me"); return attempt(false); }
    if (!res.ok || !res.body) {
      let detail = `HTTP ${res.status}`;
      try { const j = await res.json() as { detail?: string }; if (j.detail) detail = j.detail; } catch { /* ignore */ }
      throw new Error(detail);
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        const chunk = buf.slice(0, idx); buf = buf.slice(idx + 2);
        for (const line of chunk.split("\n")) {
          if (!line.startsWith("data:")) continue;
          try { onEvent(JSON.parse(line.slice(5).trim()) as Record<string, unknown>); } catch { /* skip */ }
        }
      }
    }
  };
  await attempt(true);
}

/** Downscale an image (data URL or File) to ≤ maxSide px and re-encode as JPEG. */
export async function fitImage(src: string | Blob, maxSide = 1600, quality = 0.85): Promise<{ dataUrl: string; w: number; h: number }> {
  const url = typeof src === "string" ? src : URL.createObjectURL(src);
  try {
    const img = await new Promise<HTMLImageElement>((ok, bad) => {
      const i = new Image(); i.onload = () => ok(i); i.onerror = () => bad(new Error("not a readable image")); i.src = url;
    });
    const k = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.round(img.naturalWidth * k), h = Math.round(img.naturalHeight * k);
    const c = document.createElement("canvas"); c.width = w; c.height = h;
    const g = c.getContext("2d")!;
    g.fillStyle = "#fff"; g.fillRect(0, 0, w, h);      // PNG transparency → white, not black, in JPEG
    g.drawImage(img, 0, 0, w, h);
    return { dataUrl: c.toDataURL("image/jpeg", quality), w, h };
  } finally {
    if (typeof src !== "string") URL.revokeObjectURL(url);
  }
}
